import PropTypes from "prop-types";

const PollResults = ({ results, respondents = [] }) => {
  // Calculate total votes across all options for the progress bar width
  const totalVotes = results.reduce((sum, r) => sum + (r.votes ?? 0), 0);

  // Find the winning option (highest votes) to bold the name
  const maxVotes = Math.max(...results.map((r) => r.votes ?? 0), 0);

  return (
    <div className="text-left rounded-lg border  p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-black heading-regular">Real-time Results</h2>
        <div className="flex items-center gap-1 text-small text-lighterGrey">
          {/* Updating live indicator */}
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-primary animate-spin"
            style={{ animationDuration: "3s" }}>
            <path d="M21 2v6h-6" />
            <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
            <path d="M3 22v-6h6" />
            <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
          </svg>
          <span>Updating live</span>
        </div>
      </div>

      <div className="space-y-6">
        {results.map((result) => {
          // Use the percentage from the API when available; otherwise derive it
          const percentage =
            result.percentage != null
              ? result.percentage
              : totalVotes > 0
                ? Math.round(((result.votes ?? 0) / totalVotes) * 100)
                : 0;

          const isLeading = (result.votes ?? 0) === maxVotes && maxVotes > 0;

          return (
            <div key={result.candidateID}>
              {/* Name row */}
              <div className="flex items-center justify-between mb-2">
                <span
                  className={`text-small ${isLeading ? "font-bold text-darkGrey" : "font-normal text-darkGrey"}`}>
                  {result.name}
                </span>
                <span className={`font-bold ${isLeading ? "text-primary" : "text-lighterGrey"} heading-regular`}>
                  {percentage}%
                </span>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-gray-200 rounded-full h-2.5 mb-1">
                <div
                  className="bg-primary h-2.5 rounded-full transition-all duration-500"
                  style={{ width: `${percentage}%` }}
                />
              </div>

              {/* Vote count */}
              <p className="text-small text-lighterGrey">
                {(result.votes ?? 0).toLocaleString()} total votes
              </p>
            </div>
          );
        })}

        {results.length === 0 && (
          <p className="text-center text-small text-lighterGrey py-4">
            No results yet.
          </p>
        )}
      </div>

      {/* Suppress unused-variable warning for respondents — available for future avatar use */}
      {respondents.length > 0 && null}
    </div>
  );
};

PollResults.propTypes = {
  results: PropTypes.arrayOf(
    PropTypes.shape({
      candidateID: PropTypes.string,
      name: PropTypes.string,
      votes: PropTypes.number,
      percentage: PropTypes.number,
    })
  ),
  respondents: PropTypes.arrayOf(
    PropTypes.shape({
      optionID: PropTypes.string,
      profileImage: PropTypes.string,
    })
  ),
};

export default PollResults;
